// Block Company Timeline: prev/next arrows and the year tabs switch which year's panel shows -
// looping at both ends, no autoplay. Every panel is server-rendered (BlockCompanyTimeline.cshtml)
// and only toggled [hidden] here.
//
// Tabs pattern (WAI-ARIA APG, automatic activation): Left/Right move to and select the
// previous/next year (looping), Home/End the first/last; only the active tab is in the tab order
// (roving tabindex).
//
// The blue line over the years ("blue slides out and blue slides in, depending on direction" -
// Taiga spec): each year has its own fill (block-company-timeline.css ::before), and only one is
// filled at a time. Moving it slides the fill out of the year it leaves and into the one it
// arrives at, both towards the direction of travel - forward (next, ArrowRight, a later year,
// and the loop from the newest back to the oldest) exits right and enters from the left; back
// is the mirror image. Same mechanism as Block Map's continent rows (block-map.ts
// animateSelection), via button-hover.ts's slideFillIn/slideFillOut. Hovering a year moves the
// fill there and leaving the row moves it back to the active year; touch is ignored for hover,
// like the button hover fill.
//
// Mobile widths (Figma 1841:6418): the arrows move to the bottom, and the whole block can also be
// swiped (touch only - finger left = next, looping like the arrows; swipe.ts). The year row can't
// be scrolled by hand there (CSS overflow-x: hidden, so it doesn't compete with the block's
// swipe); it follows the selection (centred - see scrollTabIntoView). A sticky hint at the bottom
// (.__swipe-hint) shows that the block can be swiped: once per page view, when the year first
// comes into view (see showSwipeHint) - and a swipe hides it at once (.is-swiped). Nothing is
// stored.

import { trackEngageEvent } from "../analytics/engage-events";
import { type Edge, slideFillIn, slideFillOut } from "./button-hover";
import { bindScrollRow } from "./scroll-row-wheel";
import { bindSwipe } from "./swipe";

const MOBILE_QUERY = "(max-width: 767px)";

// The swipe hint's fade (block-company-timeline.css .__swipe-hint transition) and how long it then
// stays. UNVERIFIED: the 3s is the user's call, the 0.3s fade our own pick.
const HINT_FADE_MS = 300;
const HINT_VISIBLE_MS = 3000;

// Panel height glide when switching years (see animatePanelHeight). UNVERIFIED: our own pick, a
// touch slower than the 0.2s hover fills since it moves a larger distance.
const PANEL_HEIGHT_MS = 300;

// Year count when switching (see countYear). UNVERIFIED: our own pick - the same total time
// whether it's one year or ten, so a long jump just counts faster.
const YEAR_COUNT_MS = 500;

// Body text and company rows fade on every switch (see fadeInContent) - the same 0.7s as the
// site's scroll-in fade, the rows one COMPANY_STAGGER_MS after another (block-company-timeline.css
// uses the same step for the scroll-in fade).
const TEXT_FADE_MS = 700;
const COMPANY_STAGGER_MS = 80;

function easeOutCubic(t: number): number {
    return 1 - (1 - t) ** 3;
}

class CompanyTimeline {
    private readonly root: HTMLElement;
    private readonly scroller: HTMLElement;
    private readonly tablist: HTMLElement;
    private readonly tabs: HTMLButtonElement[];
    private readonly panels: HTMLElement[];
    private readonly mobile = window.matchMedia(MOBILE_QUERY);
    // Swiping is touch only (swipe.ts), so the hint is too - not in a narrow desktop window.
    private readonly touch = window.matchMedia("(pointer: coarse)");
    private activeIndex: number;
    // The year whose fill is in (the active one, or the hovered one while hovering).
    private filledIndex: number;
    private fillToken = 0;
    private heightAnimation: Animation | null = null;
    private contentFades: Animation[] = [];
    private yearCount: { frame: number; heading: HTMLElement; finalText: string } | null = null;

    constructor(root: HTMLElement) {
        this.root = root;
        this.scroller = root.querySelector<HTMLElement>("[data-company-timeline-scroller]")!;
        this.tablist = root.querySelector<HTMLElement>("[data-company-timeline-tablist]")!;
        this.tabs = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-company-timeline-tab]"));
        this.panels = Array.from(root.querySelectorAll<HTMLElement>("[data-company-timeline-panel]"));
        this.activeIndex = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute("aria-selected") === "true"));
        // The server-rendered active year is filled by CSS ([aria-selected=true], for no JS) -
        // made inline here like every fill moveFill sets, so the first move treats it the same:
        // otherwise flipping aria-selected started its CSS transition back to the left before
        // slideFillOut sent it right (a visible twitch), and an interrupted move's cleanup, which
        // looks for the inline value, missed it.
        this.filledIndex = this.activeIndex;
        this.tabs[this.activeIndex]?.style.setProperty("--button-hover-x", "0");

        root.querySelector("[data-company-timeline-prev]")?.addEventListener("click", () => this.select(this.activeIndex - 1));
        root.querySelector("[data-company-timeline-next]")?.addEventListener("click", () => this.select(this.activeIndex + 1));

        this.tabs.forEach((tab, index) => {
            tab.addEventListener("click", () => this.select(index));
            tab.addEventListener("pointerenter", (event) => {
                if (event.pointerType !== "touch") {
                    this.moveFill(index, index > this.filledIndex);
                }
            });
        });
        this.tablist.addEventListener("pointerleave", (event) => {
            if (event.pointerType !== "touch") {
                this.moveFill(this.activeIndex, this.activeIndex > this.filledIndex);
            }
        });
        this.tablist.addEventListener("keydown", (event) => this.onKeydown(event));

        if (this.tabs.length > 1) {
            // Shows the swipe hint (CSS, mobile widths only) - not without JS, where swiping does nothing.
            root.classList.add("is-swipeable");
            bindSwipe(root, {
                enabled: () => this.mobile.matches,
                onSwipe: (direction) => {
                    this.select(this.activeIndex + (direction === "left" ? 1 : -1));
                    root.classList.add("is-swiped");
                },
            });
            this.showSwipeHint();
        }

        bindScrollRow(this.scroller);
        this.scrollTabIntoView(this.activeIndex, "instant");

        // On resize the visible part of the row changes with the scroller (also across the mobile
        // breakpoint, where it goes out to the block's edges), which can leave the active year
        // off centre or out of view.
        if ("ResizeObserver" in window) {
            new ResizeObserver(() => this.scrollTabIntoView(this.activeIndex, "instant")).observe(this.scroller);
        }
    }

    // index may run past either end (prev from the first year, next from the last): it wraps
    // around, and the direction of travel follows the unwrapped value - next is always forward,
    // also when it loops from the newest year back to the oldest.
    private select(index: number, focusTab = false): void {
        const count = this.tabs.length;
        const next = ((index % count) + count) % count;
        const forward = index > this.activeIndex;

        if (next !== this.activeIndex) {
            this.tabs.forEach((tab, i) => {
                const isActive = i === next;
                tab.setAttribute("aria-selected", String(isActive));
                tab.tabIndex = isActive ? 0 : -1;
            });
            // Measured before the switch, mid-animation included (getBoundingClientRect reflects an
            // in-flight height), so rapid clicks glide on from wherever the last one had got to.
            const fromHeight = this.panels[this.activeIndex]?.getBoundingClientRect().height ?? 0;
            this.panels.forEach((panel, i) => {
                panel.hidden = i !== next;
            });
            this.animatePanelHeight(this.panels[next], fromHeight);
            this.countYear(this.panels[next], this.yearAt(this.activeIndex), this.yearAt(next));
            this.fadeInContent(this.panels[next]);
            this.activeIndex = next;
            trackEngageEvent({ category: "Timeline", action: "Select", label: this.tabs[next]?.textContent?.trim() ?? "" });
        }

        // A click on the hovered year finds the fill already there (filledIndex) - no move.
        this.moveFill(next, forward);
        this.scrollTabIntoView(next, "smooth");
        if (focusTab) {
            this.tabs[next]?.focus({ preventScroll: true });
        }
    }

    // Fades the swipe hint in once the year (the big heading, all of it) is on screen - the moment
    // its own scroll-in fade plays - leaves it HINT_VISIBLE_MS, then fades it out again. Once per
    // page view, and only at mobile widths on a touch screen (elsewhere the hint isn't shown, and a
    // visit there shouldn't use it up); not at all once the visitor has swiped already. Without
    // IntersectionObserver it never shows.
    private showSwipeHint(): void {
        if (!("IntersectionObserver" in window)) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            if (this.root.classList.contains("is-swiped")) {
                observer.disconnect();
                return;
            }
            if (!this.mobile.matches || !this.touch.matches || !entries.some((entry) => entry.isIntersecting)) {
                return;
            }
            observer.disconnect();
            this.root.classList.add("is-hinting");
            setTimeout(() => this.root.classList.remove("is-hinting"), HINT_FADE_MS + HINT_VISIBLE_MS);
        }, { threshold: 1 });
        // Every panel's: only the shown one can intersect.
        this.root.querySelectorAll(".block-company-timeline__year-heading").forEach((heading) => observer.observe(heading));
    }

    // Years have different amounts of content, so switching would make the year tabs below jump up
    // or down - most visibly on mobile, where the block has no min-height to absorb it. The new
    // panel instead grows/shrinks from the old panel's height to its own, and the tabs (next in
    // the flow) glide along. Content is clipped meanwhile so a taller panel doesn't spill over the
    // tabs. User-initiated, so not a layout shift for CLS. Instant with reduced motion.
    private animatePanelHeight(panel: HTMLElement | undefined, fromHeight: number): void {
        this.heightAnimation?.cancel();
        this.heightAnimation = null;
        if (!panel || fromHeight === 0 || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        const toHeight = panel.getBoundingClientRect().height;
        if (Math.abs(toHeight - fromHeight) < 1) {
            return;
        }

        panel.style.overflow = "clip";
        const animation = panel.animate(
            [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
            { duration: PANEL_HEIGHT_MS, easing: "ease-in-out" },
        );
        this.heightAnimation = animation;
        const done = (): void => {
            panel.style.overflow = "";
        };
        animation.addEventListener("finish", done);
        animation.addEventListener("cancel", done);
    }

    // The year's body text and company rows fade in on every switch, also when that year has been
    // shown (and faded in) before - the site's scroll-in fade (fade-in.ts/scroll-reveal.ts) only
    // ever plays once per element. Same look as that one (globals.css: opacity, 0.7s ease-out),
    // the rows staggered like their scroll-in fade. Everything inside is marked as already
    // revealed first, so the one-off scroll fade can't hold it at opacity 0 or play a second fade
    // on top. Instant with reduced motion.
    private fadeInContent(panel: HTMLElement | undefined): void {
        this.contentFades.forEach((animation) => animation.cancel());
        this.contentFades = [];
        if (!panel) {
            return;
        }

        // Without the scroll fade's own CSS transition: switching "leave" -> "entered" would
        // otherwise start that 0.7s fade, and the big year (which counts instead) must just show.
        // All written first, then one forced style flush for the lot, then the transitions back -
        // not a flush per element.
        const revealed = Array.from(panel.querySelectorAll<HTMLElement>("[data-scroll-state]"));
        revealed.forEach((element) => {
            element.style.transition = "none";
            element.dataset.scrollState = "entered";
        });
        void panel.offsetWidth;
        revealed.forEach((element) => {
            element.style.transition = "";
        });
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        const fade = (element: Element, delay: number): void => {
            this.contentFades.push(element.animate([{ opacity: 0 }, { opacity: 1 }], {
                duration: TEXT_FADE_MS,
                delay,
                easing: "ease-out",
                // Hidden during its delay too, not shown and then blinked out when it starts.
                fill: "backwards",
            }));
        };
        const text = panel.querySelector(".block-company-timeline__text");
        if (text) {
            fade(text, 0);
        }
        panel.querySelectorAll(".block-company-timeline__companies > li").forEach((row, i) => fade(row, i * COMPANY_STAGGER_MS));
    }

    private yearAt(index: number): number {
        return Number(this.tabs[index]?.textContent?.trim());
    }

    // The new panel's big year counts through every year in between (2016 -> 2017 -> ... -> 2020;
    // down when looping back from the newest to the oldest), rather than swapping one year for
    // the other. The server-rendered text is the final value, so this only ever runs from an old
    // year to it; an interrupted count (rapid clicks) snaps its heading back to its own year.
    // The digits are tabular, so the width doesn't jitter while counting. Instant with reduced
    // motion. The tabpanel isn't a live region, so screen readers aren't fed the in-between years.
    private countYear(panel: HTMLElement | undefined, from: number, to: number): void {
        if (this.yearCount) {
            cancelAnimationFrame(this.yearCount.frame);
            this.yearCount.heading.textContent = this.yearCount.finalText;
            this.yearCount = null;
        }

        const heading = panel?.querySelector<HTMLElement>(".block-company-timeline__year-heading");
        if (!heading || !Number.isFinite(from) || !Number.isFinite(to) || from === to
            || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        const finalText = heading.textContent ?? String(to);
        const start = performance.now();
        const step = (now: number): void => {
            const progress = Math.min((now - start) / YEAR_COUNT_MS, 1);
            if (progress < 1) {
                heading.textContent = String(Math.round(from + (to - from) * easeOutCubic(progress)));
                this.yearCount = { frame: requestAnimationFrame(step), heading, finalText };
            } else {
                heading.textContent = finalText;
                this.yearCount = null;
            }
        };
        heading.textContent = String(from);
        this.yearCount = { frame: requestAnimationFrame(step), heading, finalText };
    }

    private onKeydown(event: KeyboardEvent): void {
        const targets: Record<string, number> = {
            ArrowLeft: this.activeIndex - 1,
            ArrowRight: this.activeIndex + 1,
            Home: 0,
            End: this.tabs.length - 1,
        };
        const target = targets[event.key];
        if (target === undefined) {
            return;
        }
        event.preventDefault();
        this.select(target, true);
    }

    // Slides the blue fill from the year that has it to `to`: in from the left and out through the
    // right going forward, the mirror image going back. slideFillIn snaps the arriving fill to its
    // start edge now and starts moving it on the next frame - the leaving fill starts on that same
    // frame so both move together (as Block Map does). A newer move cancels a still-pending
    // slide-out (fillToken), and any year left filled by an interrupted move is cleared too.
    // CSS skips the transition with reduced motion, so the fill then just jumps.
    private moveFill(to: number, forward: boolean): void {
        const from = this.filledIndex;
        const arriving = this.tabs[to];
        if (to === from || !arriving) {
            return;
        }

        const token = ++this.fillToken;
        const entry: Edge = forward ? "left" : "right";
        const exit: Edge = forward ? "right" : "left";
        const leaving = this.tabs[from];
        this.filledIndex = to;

        slideFillIn(arriving, entry);
        requestAnimationFrame(() => {
            if (this.fillToken !== token) {
                return;
            }
            if (leaving) {
                slideFillOut(leaving, exit);
            }
            this.tabs.forEach((tab, i) => {
                if (i !== to && i !== from && tab.style.getPropertyValue("--button-hover-x") === "0") {
                    slideFillOut(tab, exit);
                }
            });
        });
    }

    // Centres the year in the row - scrolling only the row, never the page (scrollIntoView could
    // also scroll vertically). The browser clamps scrollLeft to the row's range, so near either
    // end the first/last year stops at the edge rather than in the middle: the content edge on
    // mobile too, where the row goes out to the block's edges but its padding-inline
    // (block-company-timeline.css) is part of that range. Rows that fit don't scroll at all.
    // Measured on screen (getBoundingClientRect), not offsetLeft against scrollLeft: the scroller
    // isn't positioned, so a tab's offsetLeft counts from further out and included the section's
    // padding - the first year was left 16px cut off on mobile.
    private scrollTabIntoView(index: number, behavior: ScrollBehavior): void {
        const tab = this.tabs[index];
        if (!tab) {
            return;
        }
        const view = this.scroller.getBoundingClientRect();
        const rect = tab.getBoundingClientRect();
        const offset = (rect.left + rect.width / 2) - (view.left + view.width / 2);
        this.scroller.scrollTo({ left: this.scroller.scrollLeft + offset, behavior });
    }
}

export function initBlockCompanyTimeline(): void {
    document.querySelectorAll<HTMLElement>("[data-company-timeline]").forEach((root) => {
        if (root.querySelector("[data-company-timeline-tab]")) {
            new CompanyTimeline(root);
        }
    });
}
